import React, { useState, useEffect, useCallback } from "react";
import { AlertCircle, FileWarning, X, Loader2 } from "lucide-react";
import SignatureCanvas from "./SignatureCanvas";
import { FileCheck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating banner shown to any logged-in staff member who has stale
 * (un-resigned) company policies. Appears on Admin + Ops portals.
 *
 * Props:
 *   authAxios — authenticated axios instance
 *   employeeName — optional, used to prefill the typed-name field
 */
const PolicyResignBanner = ({ authAxios, employeeName = "" }) => {
  const [pending, setPending] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [activePolicy, setActivePolicy] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API_URL}/api/me/policy-resign-pending`);
      setPending(data.pending || []);
    } catch {}
  }, [authAxios]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 5 * 60 * 1000); // poll every 5 min
    return () => clearInterval(t);
  }, [fetchPending]);

  if (dismissed || pending.length === 0) return null;

  const earliest = pending.reduce((min, p) => {
    const d = new Date(p.deadline).getTime();
    return !min || d < min ? d : min;
  }, null);
  const daysLeft = earliest ? Math.max(0, Math.ceil((earliest - Date.now()) / (1000 * 60 * 60 * 24))) : 7;
  const urgent = daysLeft <= 2;

  return (
    <>
      <div
        className="fixed top-4 right-4 z-[95] w-[380px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border-2 overflow-hidden"
        style={{ borderColor: urgent ? "#DC283C" : "#F59E0B" }}
        data-testid="policy-resign-banner"
      >
        <div
          className="px-4 py-3 flex items-center gap-2 text-white"
          style={{ background: urgent ? "linear-gradient(135deg,#DC283C,#E83E8C)" : "linear-gradient(135deg,#F59E0B,#E83E8C)" }}
        >
          <AlertCircle size={18} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Policy updates require your signature</p>
            <p className="text-[10px] text-white/80">
              {pending.length} updated {pending.length === 1 ? "policy" : "policies"} · {daysLeft} day{daysLeft === 1 ? "" : "s"} left to re-sign
            </p>
          </div>
          <button onClick={() => setDismissed(true)} className="text-white/80 hover:text-white" data-testid="resign-banner-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-3 space-y-1.5 max-h-[50vh] overflow-y-auto">
          {pending.map((p) => {
            const d = new Date(p.deadline);
            const dLeft = Math.max(0, Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
            return (
              <div
                key={p.key}
                className="flex items-center gap-2 p-2 border border-gray-100 rounded-xl hover:border-[#E83E8C]/30 cursor-pointer"
                onClick={() => setActivePolicy(p)}
                data-testid={`resign-row-${p.key}`}
              >
                <FileWarning size={16} className="text-[#E83E8C] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#1E1B4B] truncate">{p.title}</p>
                  <p className="text-[10px] text-gray-400">
                    v{p.signed_version} → <b className="text-[#E83E8C]">v{p.current_version}</b> · {dLeft}d left
                  </p>
                </div>
                <button className="px-2.5 py-1 rounded-lg bg-[#E83E8C] text-white text-[10px] font-bold hover:bg-[#c22d78]">
                  Re-sign
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {activePolicy && (
        <ResignModal
          policy={activePolicy}
          prefillName={employeeName}
          authAxios={authAxios}
          onClose={() => setActivePolicy(null)}
          onSigned={() => { setActivePolicy(null); fetchPending(); }}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Re-sign Modal (compact — not a full wizard, same backend call)
// ─────────────────────────────────────────────────────────────
const ResignModal = ({ policy, prefillName, authAxios, onClose, onSigned }) => {
  const [typedName, setTypedName] = useState(prefillName || "");
  const [sig, setSig] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const canSign = agreed && typedName.trim().length >= 2 && sig.length > 200;

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      await authAxios.post(`${API_URL}/api/onboarding/policies/${policy.key}/sign`, {
        signature_image: sig, typed_name: typedName.trim(), agreed,
      });
      onSigned();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to sign — please retry.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="resign-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] text-white flex items-start justify-between">
          <div>
            <h3 className="font-bold text-sm">{policy.title}</h3>
            <p className="text-[10px] text-white/70">
              Re-sign — Version {policy.current_version} (was {policy.signed_version})
              {policy.notes ? ` · ${policy.notes}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="resign-modal-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <iframe
            title={policy.title}
            src={`${API_URL}/api/onboarding/policies/${policy.key}/view#view=FitH`}
            style={{ width: "100%", height: "40vh", border: "none" }}
          />
          <div className="p-4 border-t space-y-3 bg-gray-50">
            <div>
              <label className="text-xs font-semibold text-[#1E1B4B]">Full legal name</label>
              <input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                data-testid="resign-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1E1B4B]">Draw your signature</label>
              <div className="mt-1">
                <SignatureCanvas onChange={setSig} height={120} />
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="accent-[#E83E8C] mt-0.5" data-testid="resign-agree" />
              <span className="text-[11px] text-gray-600">
                I, <b>{typedName || "______"}</b>, confirm that I have read, understood and agree to
                the updated <b>{policy.title}</b> (version {policy.current_version}).
              </span>
            </label>
            {error && <p className="text-xs text-red-500">{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200">Cancel</button>
              <button
                onClick={submit}
                disabled={!canSign || submitting}
                className="px-4 py-1.5 text-xs rounded-lg bg-[#E83E8C] text-white font-bold disabled:opacity-40 flex items-center gap-1"
                data-testid="resign-submit"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <FileCheck size={12} />}
                {submitting ? "Signing…" : "Submit Re-sign"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyResignBanner;
